import { Link, useParams } from "react-router-dom";
import { AiGeneratedFlag, Badge, Card, CardBody, CardHeader, ConfidenceBadge, StudioState } from "@odingard/design-system";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { attentionApi } from "../../lib/api";

function label(value: string): string {
  return value.replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function applicationRoute(application: string): string {
  return `/studio/${application.replaceAll("_", "-")}`;
}

export function ContextBridge() {
  const { subjectType = "", subjectId = "" } = useParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const state = useAsync(
    () => attentionApi.bridge(orgId, subjectType, subjectId),
    [orgId, subjectType, subjectId],
  );
  if (state.loading) return <StudioState state="loading" description="Loading related context…" />;
  if (state.error) {
    const stateName = state.error.status === 401 || state.error.status === 403 ? "unauthorized" : "error";
    return <StudioState state={stateName} description={state.error.message} onRetry={state.reload} />;
  }
  if (!state.data || state.data.mode === "unavailable") {
    return <StudioState state="dependency_unavailable" title="Context unavailable" description="We could not ask the shared Context Bridge. Try again when the service returns." onRetry={state.reload} />;
  }
  const bridge = state.data.data;
  const unsupported = !bridge.origin.route;
  if (unsupported) {
    return (
      <StudioState
        state="unsupported"
        title="This context is not supported yet"
        description="The subject is known to Studio, but no owning application route is registered."
      >
        <Link to="/studio">Back to Studio</Link>
      </StudioState>
    );
  }
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Context Bridge</h1>
          <p>Related business context for {bridge.subject.label ?? label(bridge.subject.type)}.</p>
        </div>
      </div>
      <Card>
        <CardHeader title={bridge.subject.label ?? label(bridge.subject.type)} />
        <CardBody>
          <p>{label(bridge.subject.type)} · {bridge.subject.owningApplication ? label(bridge.subject.owningApplication) : "Application not registered"}</p>
          {bridge.origin.route ? <Link to={bridge.origin.route}>Open in {label(bridge.origin.application ?? "the owning application")}</Link> : null}
        </CardBody>
      </Card>
      <section className="section" aria-labelledby="related-context-heading">
        <h2 id="related-context-heading">Related context</h2>
        {bridge.related.length === 0 ? (
          <Card><CardBody><p className="muted-note">No related objects are available for this subject.</p></CardBody></Card>
        ) : (
          <div className="ods-stack">
            {bridge.related.map((entry) => {
              const destination = applicationRoute(entry.application);
              const unavailable = entry.state === "unavailable" || entry.count === null;
              return (
                <Card key={`${entry.application}:${entry.objectType}:${entry.basis}`}>
                  <CardBody>
                    <div className="ods-row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                      <div>
                        <h3 style={{ margin: 0 }}>{label(entry.objectType)}</h3>
                        <p className="muted-note" style={{ margin: "var(--ods-space-1) 0 0" }}>{label(entry.application)}</p>
                      </div>
                      <div className="ods-row" style={{ gap: "var(--ods-space-2)" }}>
                        {entry.basis === "derived" ? <><AiGeneratedFlag />{typeof entry.confidence === "number" ? <ConfidenceBadge value={entry.confidence} /> : null}</> : null}
                        <Badge tone={unavailable ? "warning" : "neutral"}>{unavailable ? "Unavailable" : `${entry.count} ${entry.count === 1 ? "object" : "objects"}`}</Badge>
                      </div>
                    </div>
                    <p className="muted-note">{entry.basis === "derived" ? "Derived relationship" : "Recorded relationship"}</p>
                    <Link to={destination}>Open {label(entry.application)}</Link>
                  </CardBody>
                </Card>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
}
